<script lang="ts" setup>
import {
  computed,
  defineOgImageComponent,
  reactive,
  ref,
  useAsyncData,
  useHead,
  useMaps,
  useRoute,
  useRouter,
  useStorage,
  watch,
} from '#imports';
const { fetchMaps, fetchDestinations, fetchCategories } = useMaps();
const search = ref('');
const filters = reactive({
  country: [],
  city: [],
  category: [],
});

const { formatedImageUrl } = useStorage();

// handle url param filters
const route = useRoute();
const normalizeQueryParam = (
  param: string | string[] | undefined,
): string[] => {
  if (!param) return [];
  if (Array.isArray(param)) return param;
  return param.includes('|') ? param.split('|') : [param];
};
// init filters
filters.country = normalizeQueryParam(route.query.country as string);
filters.city = normalizeQueryParam(route.query.city as string);
filters.category = normalizeQueryParam(route.query.category as string);
if (route.query.search && typeof route.query.search === 'string') {
  search.value = route.query.search;
}

// Make sure fetchMaps receives arrays and includes initial query params
const { data: maps, refresh } = await useAsyncData('maps', () =>
  fetchMaps(
    {
      country: filters.country,
      city: filters.city,
      category: filters.category,
    },
    search.value,
  ),
);
const { data: destinations } = await useAsyncData('destination', () =>
  fetchDestinations(),
);
const { data: categories } = await useAsyncData('categories', () =>
  fetchCategories(),
);

useHead(() => ({
  title: 'Explore Mappr',
  meta: [
    {
      name: 'description',
      content:
        'Maps, Spots and unique experiences; Explore curated lists from local experts and global creators',
    },
  ],
}));

defineOgImageComponent('Default', {
  title: 'Explore Mappr',
  description:
    'Maps, Spots and unique experiences; Explore curated lists from local experts and global creators',
});

// Watch for changes after initial load
watch(
  [search, filters],
  () => {
    const router = useRouter();
    const query = {
      country: filters.country.length ? filters.country.join('|') : undefined,
      city: filters.city.length ? filters.city.join('|') : undefined,
      category: filters.category.length
        ? filters.category.join('|')
        : undefined,
      search: search.value || undefined,
    };

    router.push({ query });
    refresh();
  },
  { deep: true },
);

const resetFiltersAndSearchTerm = () => {
  filters.country = [];
  filters.city = [];
  filters.category = [];
  search.value = '';
};

const filteredCities = computed(() => {
  if (!destinations.value) return [];
  if (!filters.country.length)
    return destinations.value.map((item) => ({
      label: item.name,
      value: item.name,
    }));
  return destinations.value
    .filter((item) => filters.country.includes(item.country))
    .map((item) => ({ label: item.name, value: item.name }));
});

const uniqueCountries = computed(() => {
  if (!destinations.value) return [];
  const uniqueCountrySet = new Set(
    destinations.value.map((item) => item.country),
  );
  return Array.from(uniqueCountrySet).map((country) => ({
    label: country,
    value: country,
  }));
});

// reset options on country change
watch(
  () => filters.country,
  () => {
    filters.city = [];
  },
  { deep: true },
);
</script>

<template>
  <main v-if="maps" class="maps-page">
    <header class="page-header">
      <MPSectionHeader
        subtitle="Creator Curated Maps"
        title="Find Your Next Adventure"
        summary="Search for the destination you want, and view all our unique maps and spots"
      />
    </header>
    <MPViewportSpill class="controls">
      <div class="page-search">
        <MPInput
          v-model="search"
          label="Search maps"
          placeholder="Search Maps"
          name="search"
          hide-label
          rounded
        />
      </div>
      <div class="page-filters">
        <MPSelect
          v-if="destinations"
          v-model="filters.country"
          label="Filter by"
          placeholder="Country"
          multiple
          :options="uniqueCountries"
          hide-label
          rounded
        />
        <MPSelect
          v-if="destinations"
          v-model="filters.city"
          label="City"
          placeholder="City"
          multiple
          :options="filteredCities"
          hide-label
          rounded
        />
        <MPSelect
          v-if="categories"
          v-model="filters.category"
          label="Category"
          placeholder="Category"
          multiple
          :options="categories.map(item => ({ label: item.name, value: item.name }))"
          hide-label
          rounded
        />
      </div>
    </MPViewportSpill>
    <TransitionGroup v-if="maps.length > 0" name="list" class="maps-list" tag="div">
      <div v-for="map in maps" :key="map.id">
        <MPMapCard :map="map" />
      </div>
    </TransitionGroup>
    <div v-else class="no-results">
      <h3>No results found</h3>
      <p>
        We couldnt match any results for the search and filters you've entered
      </p>
      <MPButton version="secondary" label="Reset filters" @click="resetFiltersAndSearchTerm()"/>
    </div>
  </main>
</template>

<style lang="postcss" scoped>
@import '@/assets/css/breakpoints.pcss';
@import '@/assets/css/typography.pcss';
.maps-page {
  margin: 0 auto;
  max-width: var(--default-content-width);
  display: grid;
  align-items: center;
}

.page-header {
  margin: 1.5rem 0;
  @mixin screen md {
    margin: 3rem 0 1.5rem;
  }
}

.controls {
  width: fit-content;
  max-width: 100vw;
  display: inline-flex;
  align-items: baseline;
  margin: 0 auto;
  gap: 0.75rem;
}

.page-search {
  width: 10rem;
  padding-left: 1rem;
  @mixin screen md {
    width: 20rem;
  }
}

.page-filters {
  display: inline-flex;
  align-items: end;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-right: 1rem;
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 5rem 0;
  text-align: center;
  max-width: 20rem;
  margin: 0 auto;
  h3 {
    margin: 0;
  }
}

.maps-list {
  display: grid;
  grid-template-columns: repeat(1fr);
  gap: 3rem 1rem;
  margin: 2rem 0 8rem;
  padding: 0 1rem;
  @mixin screen xs {
    grid-template-columns: repeat(2, 1fr);
  }
  @mixin screen sm {
    grid-template-columns: repeat(3, 1fr);
  }
  @mixin screen md {
    grid-template-columns: repeat(3, 1fr);
  }
  @mixin screen lg {
    grid-template-columns: repeat(4, 1fr);
  }
  @mixin screen xl {
    grid-template-columns: repeat(4, 1fr);
  }
}

.list-enter-active,
.list-leave-active {
  transition: all 0.25s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
